/**
 * portfolio DB 破損検知時の Sentry 報告と local cache 復旧処理を共通化する。
 */
import { clearPortfolioDataFromLocalStorage } from '@/localStorage'
import { captureException, flush, setContext } from '@sentry/react'

/** 破損を検知した箇所を Sentry 上で区別するための識別子。 */
export type CorruptedDatabaseSite =
  /** database file access 前の整合性検証 */
  | 'database-file-access'
  /** setupPortfolio の同一 portfolio 継続時の migration */
  | 'setup-portfolio-sync'
  /** setupPortfolio の portfolio 切り替え時の migration */
  | 'setup-portfolio-change'

/** reload 前に Sentry 送信完了を待つ上限時間 (ms)。 */
const sentryFlushTimeoutMs = 3_000

/** ユーザーへ表示する破損通知文言。 */
const corruptedDatabaseAlertMessage =
  'Your Portfolio Database file is corrupted. Clear local cache and reload the application.'

/**
 * 破損例外を Sentry の送信 queue へ積む。送信可否に関わらず復旧処理を止めない。
 * @param params 破損例外、検知箇所、調査用の付随情報を受け取る。
 */
const captureCorruptedDatabase = ({
  error,
  site,
  context,
}: {
  readonly error: unknown
  readonly site: CorruptedDatabaseSite
  readonly context: Readonly<Record<string, unknown>> | undefined
}): void => {
  try {
    setContext('portfolioDatabase', { site, ...context })
    captureException(error)
  } catch {
    // Sentry 送信の失敗は復旧処理を妨げない
  }
}

/**
 * Sentry envelope の送信完了を待つ。失敗や timeout でも復旧処理を止めない。
 */
const flushCorruptedDatabaseReport = async (): Promise<void> => {
  try {
    await flush(sentryFlushTimeoutMs)
  } catch {
    // Sentry 送信の失敗は復旧処理を妨げない
  }
}

/**
 * portfolio DB 破損を Sentry へ報告した上で local cache を破棄しリロードする。
 * ユーザーへの通知を遅らせないため alert を先に出し、
 * alert が閉じられた後に送信完了を待ってから reload する。
 * reload 前に送信完了を待つため、必ず await して利用する。
 * @param params 破損例外、検知箇所、調査用の付随情報を受け取る。
 */
export const handleCorruptedPortfolioDatabase = async ({
  error,
  site,
  context,
}: {
  readonly error: unknown
  readonly site: CorruptedDatabaseSite
  readonly context?: Readonly<Record<string, unknown>>
}): Promise<void> => {
  // 送信 queue へ積んでから alert する。alert が blocking な間に送信が進む
  captureCorruptedDatabase({ error, site, context })

  // local cache clear で解決する場合があるため、ユーザーへ必ず案内する
  alert(corruptedDatabaseAlertMessage)

  await flushCorruptedDatabaseReport()
  clearPortfolioDataFromLocalStorage()
  window.location.reload()
}
